<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { YardTaskCheckLocationOutcome } from '@nova/core';
import { useYardAssetPresenterV2 } from '@satellite/composables/useYardAssetPresenterV2';
import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';
import {
  getAssetContainerStatusIcon,
  getAssetContainerStatusColor,
  getAssetContainerStateIcon
} from '@satellite/helpers/yardTaskUtils';

import type { Maybe } from '@nova/core';
import type { CheckTaskCheckAssetContainerView } from '@nova/core';

const { t } = useI18n();

const props = defineProps<{
  assetId: Maybe<string>;
  checkAssetContainers?: CheckTaskCheckAssetContainerView[];
}>();

const assetIdRef = computed(() => props.assetId ?? null);
const { data: asset } = useYardAssetPresenterV2({ assetId: assetIdRef, headless: true });

const checkRecord = computed(() => props.checkAssetContainers?.[0] ?? null);

const isMissing = computed(
  () => checkRecord.value?.outcome === YardTaskCheckLocationOutcome.MISSING
);

const locationLabel = computed(() => {
  if (!checkRecord.value) {
    return t('checkAssetPanel.runCheck');
  }
  return checkRecord.value.outcome === YardTaskCheckLocationOutcome.CONFIRMED
    ? t('assetCheckWizard.summary.locationConfirmed')
    : t('assetCheckWizard.summary.locationMissing');
});

const locationIcon = computed(() => {
  if (!checkRecord.value) {
    return null;
  }
  return checkRecord.value.outcome === YardTaskCheckLocationOutcome.CONFIRMED
    ? 'mdi-map-marker-check'
    : 'mdi-map-marker-off';
});

const locationIconColor = computed(() => {
  if (!checkRecord.value) {
    return null;
  }
  return checkRecord.value.outcome === YardTaskCheckLocationOutcome.CONFIRMED ? 'success' : 'error';
});

const latestEvent = computed(() => {
  const events = asset.value?.assetContainerEvents;
  if (!events?.length) {
    return null;
  }
  return [...events].sort(
    (a, b) => new Date(b.createDateTime).getTime() - new Date(a.createDateTime).getTime()
  )[0];
});

const statusLabel = computed(() => {
  if (!checkRecord.value) {
    return t('checkAssetPanel.runCheck');
  }
  return latestEvent.value?.eventType ?? PRESENTER_VALUE_FALLBACK;
});

const statusIcon = computed(() => {
  if (!checkRecord.value || !latestEvent.value?.eventType) {
    return null;
  }
  return getAssetContainerStatusIcon(latestEvent.value.eventType);
});

const statusIconColor = computed(() => {
  if (!checkRecord.value || !latestEvent.value?.eventType) {
    return undefined;
  }
  return getAssetContainerStatusColor(latestEvent.value.eventType);
});

const stateLabel = computed(() => {
  if (!checkRecord.value) {
    return t('checkAssetPanel.runCheck');
  }
  return asset.value?.state ?? PRESENTER_VALUE_FALLBACK;
});

const stateIcon = computed(() => {
  if (!checkRecord.value || !asset.value?.state) {
    return null;
  }
  return getAssetContainerStateIcon(asset.value.state);
});
</script>

<template>
  <v-card variant="flat" border class="d-flex pa-4 gap-4" data-testid="check-asset-panel-summary">
    <div
      class="flex-1-1-0 d-flex flex-column gap-1"
      data-testid="check-asset-panel-summary-location">
      <span class="typography-body-sm-bold">{{ t('assetCheckWizard.steps.location') }}</span>
      <div class="d-flex align-center gap-1 typography-body-sm text-medium-emphasis">
        <v-icon
          v-if="locationIcon"
          :color="locationIconColor ?? undefined"
          size="1em"
          :icon="locationIcon" />
        <span>{{ locationLabel }}</span>
      </div>
    </div>

    <template v-if="!isMissing">
      <v-divider vertical />

      <div class="flex-1-1-0 d-flex flex-column gap-1">
        <span class="typography-body-sm-bold">{{ t('assetCheckWizard.steps.status') }}</span>
        <div class="d-flex align-center gap-1 typography-body-sm text-medium-emphasis">
          <v-icon v-if="statusIcon" size="1em" :color="statusIconColor" :icon="statusIcon" />
          <span>{{ statusLabel }}</span>
        </div>
      </div>

      <v-divider vertical />

      <div class="flex-1-1-0 d-flex flex-column gap-1">
        <span class="typography-body-sm-bold">{{ t('assetCheckWizard.steps.state') }}</span>
        <div class="d-flex align-center gap-1 typography-body-sm text-medium-emphasis">
          <v-icon v-if="stateIcon" size="1em" :icon="stateIcon" />
          <span>{{ stateLabel }}</span>
        </div>
      </div>
    </template>
  </v-card>
</template>
